import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import { Organization, OrgAdmin, OrgMember, Group } from '../types';

function makeCode() {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  return Array.from(crypto.getRandomValues(new Uint8Array(8)))
    .map(b => chars[b % chars.length])
    .join('');
}

interface OrganizationState {
  organizations: Organization[];
  myAdminOrgIds: Set<string>;
  orgAdminsList: OrgAdmin[];
  orgMembersList: OrgMember[];
  orgGroups: Record<string, Group[]>;
  myAdminGroups: Group[];
  groupOrgInfo: Record<string, Organization | null>;

  fetchMyOrganizations: (userId: string) => Promise<void>;
  fetchGroupOrganization: (groupId: string) => Promise<void>;
  fetchGroupsOrgInfo: (groupIds: string[]) => Promise<void>;
  createOrganization: (name: string, type: string, userId: string) => Promise<string | null>;
  updateOrganizationType: (organizationId: string, type: string) => Promise<string | null>;
  joinOrganizationByCode: (code: string) => Promise<string | null>;
  fetchOrgAdminsList: (organizationId: string) => Promise<void>;
  addOrgAdmin: (organizationId: string, email: string) => Promise<string | null>;
  removeOrgAdmin: (organizationId: string, userId: string) => Promise<string | null>;
  fetchOrgMembersList: (organizationId: string) => Promise<void>;
  leaveOrganization: (organizationId: string, userId: string) => Promise<string | null>;
  removeOrgMember: (organizationId: string, targetUserId: string) => Promise<string | null>;
  fetchOrgGroups: (organizationId: string) => Promise<void>;
  fetchMyAdminGroups: (userId: string) => Promise<void>;
  linkGroupToOrganization: (organizationId: string, groupId: string) => Promise<string | null>;
  unlinkGroupFromOrganization: (organizationId: string, groupId: string) => Promise<string | null>;
  setItemOrganization: (inventoryId: string, organizationId: string | null) => Promise<string | null>;
  deleteOrganization: (organizationId: string, userId: string) => Promise<string | null>;
}

export const useOrganizations = create<OrganizationState>((set, get) => ({
  organizations: [],
  myAdminOrgIds: new Set(),
  orgAdminsList: [],
  orgMembersList: [],
  orgGroups: {},
  myAdminGroups: [],
  groupOrgInfo: {},

  // Fungerer for ALLE gruppemedlemmer (ikke bare org-admin/org-medlem) — viser
  // kun at gruppen tilhører en organisasjon, ingen administrasjonsmuligheter.
  fetchGroupOrganization: async (groupId) => {
    const { data } = await supabase
      .from('organization_groups')
      .select('organizations(*)')
      .eq('group_id', groupId)
      .maybeSingle();
    const org = (data as any)?.organizations ?? null;
    set(s => ({ groupOrgInfo: { ...s.groupOrgInfo, [groupId]: org } }));
  },

  // Samme som fetchGroupOrganization, men for flere grupper i ett kall — brukes
  // til å seksjonere gruppevelgere (topbar-dropdown, «Bytt gruppe») etter
  // organisasjon for ALLE brukerens grupper, uavhengig av om brukeren selv er
  // medlem av organisasjonen som eier gruppen.
  fetchGroupsOrgInfo: async (groupIds) => {
    if (!groupIds.length) return;
    const { data } = await supabase
      .from('organization_groups')
      .select('group_id, organizations(*)')
      .in('group_id', groupIds);
    const map: Record<string, Organization | null> = {};
    (data || []).forEach((row: any) => { map[row.group_id] = row.organizations ?? null; });
    set(s => ({ groupOrgInfo: { ...s.groupOrgInfo, ...map } }));
  },

  fetchMyOrganizations: async (userId) => {
    const { data: adminRows } = await supabase.from('organization_admins').select('organization_id').eq('user_id', userId);
    const adminIds = new Set((adminRows || []).map((r: any) => r.organization_id as string));

    const { data: memberRows } = await supabase.from('organization_members').select('organization_id, organizations(*)').eq('user_id', userId);
    const orgs = (memberRows || []).map((r: any) => r.organizations).filter(Boolean);

    set({ organizations: orgs, myAdminOrgIds: adminIds });
  },

  createOrganization: async (name, type, userId) => {
    // Kjøres som én atomisk RPC (ikke separate klient-inserts) — en helt
    // fersk organisasjon har ingen admin-/medlemsrad ennå, så den ville
    // feilet organizations_select-policyen (kun admin/medlem) hvis en
    // vanlig INSERT ... RETURNING ble brukt direkte fra klienten.
    const { error } = await supabase.rpc('create_organization', { p_name: name, p_type: type || null, p_invite_code: makeCode() });
    if (error) return error.message;
    await get().fetchMyOrganizations(userId);
    return null;
  },

  updateOrganizationType: async (organizationId, type) => {
    const { error } = await supabase.from('organizations').update({ type: type || null }).eq('id', organizationId);
    if (error) return error.message;
    set(s => ({ organizations: s.organizations.map(o => o.id === organizationId ? { ...o, type: type || null } : o) }));
    return null;
  },

  joinOrganizationByCode: async (code) => {
    const { error } = await supabase.rpc('join_organization', { p_invite_code: code.toUpperCase() });
    if (error) return error.message;
    return null;
  },

  fetchOrgAdminsList: async (organizationId) => {
    const { data, error } = await supabase.rpc('list_org_admins', { p_organization_id: organizationId });
    if (!error) set({ orgAdminsList: data || [] });
  },

  addOrgAdmin: async (organizationId, email) => {
    const { error } = await supabase.rpc('add_org_admin', { p_organization_id: organizationId, target_email: email });
    if (error) return error.message;
    await get().fetchOrgAdminsList(organizationId);
    return null;
  },

  removeOrgAdmin: async (organizationId, userId) => {
    const { error } = await supabase.rpc('remove_org_admin', { p_organization_id: organizationId, target_user_id: userId });
    if (error) return error.message;
    await get().fetchOrgAdminsList(organizationId);
    return null;
  },

  // Vanlige medlemmer (ikke admin) — enkel klient-select siden organization_members
  // sin SELECT-policy allerede tillater org-admin/org-medlem å lese, og profiles
  // (i motsetning til auth.users) er trygt lesbar direkte fra klienten.
  fetchOrgMembersList: async (organizationId) => {
    const { data } = await supabase
      .from('organization_members')
      .select('*, profile:profiles(*)')
      .eq('organization_id', organizationId);
    set({ orgMembersList: data || [] });
  },

  // Fjerner deg selv fullstendig fra organisasjonen (medlem + evt. admin-status)
  // OG fra alle grupper som tilhører akkurat denne organisasjonen — atomisk RPC,
  // se supabase/migrations/20260801130000_leave_remove_org_member.sql.
  leaveOrganization: async (organizationId, userId) => {
    const { error } = await supabase.rpc('leave_organization', { p_organization_id: organizationId });
    if (error) return error.message;
    await get().fetchMyOrganizations(userId);
    return null;
  },

  removeOrgMember: async (organizationId, targetUserId) => {
    const { error } = await supabase.rpc('remove_org_member', { p_organization_id: organizationId, p_target_user_id: targetUserId });
    if (error) return error.message;
    await get().fetchOrgMembersList(organizationId);
    await get().fetchOrgAdminsList(organizationId);
    return null;
  },

  fetchOrgGroups: async (organizationId) => {
    const { data } = await supabase.from('organization_groups').select('group_id, groups(*)').eq('organization_id', organizationId);
    const groups = (data || []).map((r: any) => r.groups).filter(Boolean);
    set(s => ({ orgGroups: { ...s.orgGroups, [organizationId]: groups } }));
  },

  fetchMyAdminGroups: async (userId) => {
    const { data } = await supabase.from('group_members').select('group_id, groups(*)').eq('user_id', userId).eq('role', 'admin');
    const groups = (data || []).map((r: any) => r.groups).filter(Boolean);
    set({ myAdminGroups: groups });
  },

  linkGroupToOrganization: async (organizationId, groupId) => {
    const { error } = await supabase.from('organization_groups').insert({ organization_id: organizationId, group_id: groupId });
    if (error) return error.message;
    await get().fetchOrgGroups(organizationId);
    return null;
  },

  unlinkGroupFromOrganization: async (organizationId, groupId) => {
    const { error } = await supabase.from('organization_groups').delete().eq('organization_id', organizationId).eq('group_id', groupId);
    if (error) return error.message;
    await get().fetchOrgGroups(organizationId);
    return null;
  },

  setItemOrganization: async (inventoryId, organizationId) => {
    await supabase.from('organization_items').delete().eq('inventory_id', inventoryId);
    if (organizationId) {
      const { error } = await supabase.from('organization_items').insert({ organization_id: organizationId, inventory_id: inventoryId });
      if (error) return error.message;
    }
    return null;
  },

  deleteOrganization: async (organizationId, userId) => {
    // Kaskaderer kun til organisasjonens egne koblingstabeller — de faktiske
    // gruppene/tingene som var koblet til slettes ikke, de blir avkoblet.
    const { error } = await supabase.from('organizations').delete().eq('id', organizationId);
    if (error) return error.message;
    await get().fetchMyOrganizations(userId);
    return null;
  },
}));
